import React, { useState, useEffect } from 'react'
import { X, TrendingUp, TrendingDown, Plus, Minus, RefreshCw } from 'lucide-react'
import { portfolioAPI, kiteAPI } from '../services/api'
import LoadingSpinner from './LoadingSpinner'
import toast from 'react-hot-toast'

const OrderModal = ({
  isOpen, onClose, stock, orderType, ltp: initialLtp, existingHoldings = [],
  // Optional prefill (used by Health "Review Order" suggested-order flow). Backward-compatible.
  initialQuantity, initialPrice, initialPositionType,
}) => {
  const [quantity, setQuantity] = useState(initialQuantity || 1)
  const [orderTypeSelected, setOrderTypeSelected] = useState('LIMIT')
  const [positionType, setPositionType] = useState(initialPositionType || 'INTRADAY') // INTRADAY or LONG
  const [price, setPrice] = useState(initialPrice || 0)
  const [ltp, setLtp] = useState(initialLtp || 0)
  const [loading, setLoading] = useState(false)
  const [fetchingLtp, setFetchingLtp] = useState(false)
  const [priceAdjustment, setPriceAdjustment] = useState(0) // Percentage adjustment from LTP

  // Apply prefill when the modal opens (suggested-order flow)
  useEffect(() => {
    if (isOpen) {
      if (initialQuantity) setQuantity(initialQuantity)
      if (initialPositionType) setPositionType(initialPositionType)
      if (initialPrice) setPrice(initialPrice)
    }
  }, [isOpen, initialQuantity, initialPrice, initialPositionType])

  // Check if user has existing CNC holdings for this stock
  const existingHolding = existingHoldings.find(h => 
    h.tradingsymbol === (stock?.symbol || stock?.tradingsymbol)
  )
  const hasExistingCNC = existingHolding && existingHolding.quantity > 0

  useEffect(() => {
    if (isOpen && stock) {
      fetchFreshLTP()
    }
  }, [isOpen, stock])

  useEffect(() => {
    if (ltp > 0) {
      // Calculate price based on LTP and adjustment percentage
      const adjustedPrice = ltp * (1 + priceAdjustment / 100)
      const tickSize = 0.05
      const roundedPrice = Math.round(adjustedPrice / tickSize) * tickSize
      setPrice(Number(roundedPrice.toFixed(2)))
    }
  }, [ltp, priceAdjustment])

  const fetchFreshLTP = async () => {
    if (!stock?.symbol && !stock?.tradingsymbol) return

    setFetchingLtp(true)
    try {
      const symbol = stock.symbol || stock.tradingsymbol
      const cleanSymbol = symbol.replace('-EQ', '')
      
      const response = await kiteAPI.getQuote(cleanSymbol)
      
      if (response.data?.data?.last_price) {
        const freshLtp = response.data.data.last_price
        setLtp(freshLtp)
        toast.success(`Fresh LTP: ₹${freshLtp.toFixed(2)}`)
      } else {
        toast.error('Unable to fetch fresh LTP')
      }
    } catch (error) {
      console.error('Fresh LTP fetch error:', error)
      toast.error('Failed to fetch fresh LTP')
      // Use initial LTP if fresh fetch fails
      setLtp(initialLtp || 0)
    } finally {
      setFetchingLtp(false)
    }
  }

  const handlePlaceOrder = async () => {
    if (quantity <= 0) {
      toast.error('Please enter a valid quantity')
      return
    }

    // Validation for Intraday orders with existing CNC holdings
    if (positionType === 'INTRADAY' && hasExistingCNC && orderType === 'SELL') {
      toast.error('Cannot place intraday SELL order when you have existing CNC holdings. Use LONG position type to exit CNC holdings.')
      return
    }

    if (positionType === 'INTRADAY' && hasExistingCNC && orderType === 'BUY') {
      toast.error('Cannot place intraday BUY order when you have existing CNC holdings. This would create conflicting positions.')
      return
    }

    setLoading(true)
    try {
      const orderData = {
        tradingsymbol: stock.symbol || stock.tradingsymbol,
        exchange: stock.exchange || 'NSE',
        transaction_type: orderType,
        order_type: orderTypeSelected,
        quantity: quantity,
        product: positionType === 'INTRADAY' ? 'MIS' : 'CNC',
        validity: 'DAY'
      }

      // For CNC SELL orders, ensure we don't sell more than we have
      if (positionType === 'LONG' && orderType === 'SELL' && existingHolding) {
        const availableQuantity = existingHolding.quantity + (existingHolding.t1_quantity || 0)
        if (quantity > availableQuantity) {
          toast.error(`Cannot sell ${quantity} shares. You only have ${availableQuantity} shares available.`)
          setLoading(false)
          return
        }
      }

      if (orderTypeSelected === 'LIMIT') {
        orderData.price = price
      }

      await portfolioAPI.placeOrder(orderData)
      
      const positionTypeText = positionType === 'INTRADAY' ? 'Intraday' : 'Long'
      const actionText = positionType === 'LONG' && orderType === 'SELL' ? 'Exit' : orderType
      
      toast.success(`${positionTypeText} ${actionText} order placed successfully`)
      onClose()
    } catch (error) {
      console.error('Place order error:', error)
      toast.error(error.response?.data?.message || 'Failed to place order')
    } finally {
      setLoading(false)
    }
  }

  const adjustPrice = (direction) => {
    const step = 0.1 // 0.1% step
    const newAdjustment = direction === 'up' 
      ? priceAdjustment + step 
      : priceAdjustment - step
    setPriceAdjustment(Number(newAdjustment.toFixed(1)))
  }

  if (!isOpen) return null

  const totalValue = quantity * (orderTypeSelected === 'LIMIT' ? price : ltp)
  const isExitOrder = positionType === 'LONG' && orderType === 'SELL' && hasExistingCNC

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {isExitOrder ? 'Exit Position' : `${orderType} ${stock?.symbol || stock?.tradingsymbol}`}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Stock Info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {orderType === 'BUY' ? (
                  <TrendingUp className="w-5 h-5 text-success-600" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-danger-600" />
                )}
                <span className="font-medium">{stock?.symbol || stock?.tradingsymbol}</span>
                {hasExistingCNC && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    CNC Holdings: {existingHolding.quantity + (existingHolding.t1_quantity || 0)}
                  </span>
                )}
              </div>
              <div className="text-right">
                <div className="flex items-center space-x-2">
                  <div>
                    <p className="text-sm text-gray-600">LTP</p>
                    <p className="font-semibold">₹{ltp?.toFixed(2) || '0.00'}</p>
                  </div>
                  <button
                    onClick={fetchFreshLTP}
                    disabled={fetchingLtp}
                    className="p-1 text-blue-600 hover:text-blue-800"
                    title="Refresh LTP"
                  >
                    <RefreshCw className={`w-4 h-4 ${fetchingLtp ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Position Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Position Type
            </label>
            <select
              value={positionType}
              onChange={(e) => setPositionType(e.target.value)}
              className="input"
            >
              <option value="INTRADAY">Intraday (MIS)</option>
              <option value="LONG">Long Position (CNC)</option>
            </select>
            {positionType === 'INTRADAY' && (
              <p className="text-xs text-gray-500 mt-1">
                Day orders - positions will be auto-squared off at market close
              </p>
            )}
            {positionType === 'LONG' && (
              <p className="text-xs text-gray-500 mt-1">
                {orderType === 'SELL' && hasExistingCNC 
                  ? 'Will exit your existing CNC holdings'
                  : 'Cash and Carry - positions held until manually sold'
                }
              </p>
            )}
          </div>

          {/* Order Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Order Type
            </label>
            <select
              value={orderTypeSelected}
              onChange={(e) => setOrderTypeSelected(e.target.value)}
              className="input"
            >
              <option value="MARKET">Market</option>
              <option value="LIMIT">Limit</option>
            </select>
          </div>

          {/* Price (for limit orders) */}
          {orderTypeSelected === 'LIMIT' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Limit Price
              </label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => adjustPrice('down')}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <div className="flex-1 text-center">
                    <input
                      type="number"
                      step="0.05"
                      value={price}
                      onChange={(e) => {
                        const newPrice = parseFloat(e.target.value) || 0
                        setPrice(newPrice)
                        // Calculate adjustment percentage from LTP
                        if (ltp > 0) {
                          const adjustment = ((newPrice - ltp) / ltp) * 100
                          setPriceAdjustment(Number(adjustment.toFixed(1)))
                        }
                      }}
                      className="input text-center"
                      placeholder="Enter price"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {priceAdjustment >= 0 ? '+' : ''}{priceAdjustment.toFixed(1)}% from LTP
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => adjustPrice('up')}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantity
            </label>
            <input
              type="number"
              min="1"
              max={positionType === 'LONG' && orderType === 'SELL' && existingHolding 
                ? existingHolding.quantity + (existingHolding.t1_quantity || 0)
                : undefined
              }
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
              className="input"
              placeholder="Enter quantity"
            />
            {positionType === 'LONG' && orderType === 'SELL' && existingHolding && (
              <p className="text-xs text-gray-500 mt-1">
                Available to sell: {existingHolding.quantity + (existingHolding.t1_quantity || 0)} shares
              </p>
            )}
          </div>

          {/* Total Value */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Value:</span>
              <span className="font-semibold">₹{totalValue.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-gray-500">Product:</span>
              <span className="text-xs font-medium">
                {positionType === 'INTRADAY' ? 'MIS (Intraday)' : 'CNC (Long Position)'}
              </span>
            </div>
          </div>

          {/* Validation Warnings */}
          {positionType === 'INTRADAY' && hasExistingCNC && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                ⚠️ You have existing CNC holdings for this stock. Intraday orders may create conflicting positions.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handlePlaceOrder}
              disabled={loading || quantity <= 0 || (fetchingLtp)}
              className={`flex-1 ${
                orderType === 'BUY' ? 'btn-success' : 'btn-danger'
              } flex items-center justify-center`}
            >
              {loading ? (
                <LoadingSpinner size="small" />
              ) : (
                <>
                  {isExitOrder ? 'Exit' : orderType} {quantity} Share{quantity > 1 ? 's' : ''}
                  {positionType === 'INTRADAY' && (
                    <span className="ml-1 text-xs opacity-75">(Intraday)</span>
                  )}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrderModal